/**
 * Scroll reveal — fades top-level homepage sections up as they enter the viewport.
 *
 * Safe by design:
 *   - Bails on `prefers-reduced-motion: reduce` (no class added → no hidden state).
 *   - Only adds the `reveal-on-scroll` class via JS, so if this script fails to
 *     load, sections render normally (no FOUC, no permanently-hidden content).
 *   - Sections already in view at load skip the animation to avoid hero flash.
 *   - Each section is unobserved after its first reveal.
 */
(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  const ready = (fn) =>
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', fn, { once: true })
      : fn();

  ready(function () {
    const sections = document.querySelectorAll('main > .shopify-section');
    if (!sections.length) return;

    const viewportHeight = window.innerHeight;
    const toObserve = [];

    sections.forEach((section) => {
      // Only animate sections that have a `.section-background` child. Those
      // are the standard content sections where the cream/brand bg lives on a
      // separate element from the content, so we can hold the bg in place and
      // animate only the inner `.section` content (see base.css). Full-bleed
      // sections (hero, trust strip, marquee) lack this structure and would
      // briefly flash white through their own bg if animated — skip them.
      const bg = section.firstElementChild;
      if (!bg || !bg.classList.contains('section-background')) return;

      const rect = section.getBoundingClientRect();
      // Already visible on first paint → show immediately, no animation.
      if (rect.top < viewportHeight * 0.9) return;
      section.classList.add('reveal-on-scroll');
      toObserve.push(section);

      // Tag each direct content block as a scroll-reveal child. Modern browsers
      // (with `animation-timeline: view()` support) use this to animate each
      // block individually as it enters the viewport — fixes the "tall section
      // flashing on its lower content" problem where a single section-level
      // transition can complete before the user scrolls to read the bottom.
      // Older browsers ignore the class (their @supports CSS doesn't match)
      // and fall back to the section-level transition.
      markRevealChildren(section);
    });

    if (!toObserve.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          // entry.target is the inner `.section`; flag `.is-visible` on the
          // parent `.shopify-section` so the CSS selector matches.
          entry.target.parentElement.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      {
        // Trigger when the CONTENT enters view, not the section's top edge.
        // Tall sections can have their `.section` content several hundred px
        // below the outer section's top, so observing the outer wrapper would
        // fire the fade before the user can actually see the content.
        rootMargin: '0px',
        threshold: 0,
      }
    );

    toObserve.forEach((section) => {
      const inner = section.querySelector(':scope > .section');
      if (inner) observer.observe(inner);
    });
  });

  /**
   * Mark each direct content block of `section` with `.scroll-reveal-child`.
   *
   * Section structures vary: generic sections nest blocks inside
   * `.section-content-wrapper`, while media-with-content puts them directly
   * inside `.section`. Try the wrapper path first, fall back to inner-section
   * children (excluding background layers).
   *
   * For each candidate, drill through single-child wrappers (up to a small
   * depth) until we hit a multi-child level — that's where the actual content
   * blocks live. This handles cases like media-with-content's
   * `_content-without-appearance` block, which is a single wrapper around
   * several text/image blocks: without drilling, the whole wrapper would
   * animate as one unit, defeating per-element reveal.
   */
  function markRevealChildren(section) {
    const innerSection = section.querySelector(':scope > .section');
    if (!innerSection) return;

    let candidates;
    const wrapper = innerSection.querySelector('.section-content-wrapper');
    if (wrapper) {
      candidates = Array.from(wrapper.children);
    } else {
      candidates = Array.from(innerSection.children).filter(function (c) {
        return (
          !c.classList.contains('custom-section-background') &&
          !c.classList.contains('section-background')
        );
      });
    }

    let targets = [];
    candidates.forEach(function (c) {
      targets = targets.concat(resolveRevealTargets(c));
    });

    targets.forEach(function (t) {
      t.classList.add('scroll-reveal-child');
    });
  }

  /**
   * Drill through single-child wrappers up to 3 levels to find the level
   * where multiple sibling content blocks live. Stops at the first level
   * with 2+ children; falls back to the original candidate if no such level
   * is found within the depth limit.
   */
  function resolveRevealTargets(candidate) {
    let current = candidate;
    for (let depth = 0; depth < 3; depth++) {
      const kids = Array.from(current.children);
      if (kids.length >= 2) return kids;
      if (kids.length === 1) {
        current = kids[0];
        continue;
      }
      break;
    }
    return [candidate];
  }
})();
